'use client';

import { Caption, Heading } from '@depop/web-thrift-ui/Typography';
import { AttentionIcon } from '@depop/web-ui-kit/Icons/AttentionIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { QuizOption } from '../QuizOption';

import styles from './styles.module.css';

import { type QuizOption as QuizOptionType } from '@/modules/quiz/types';

type QuizQuestionProps = {
  title: string;
  options: QuizOptionType[];
  selectedValue: string | null;
  onSelect: (value: string) => void;
  error?: string;
  alignment: 'ladder' | 'grid';
  name: string;
};

export function QuizQuestion({
  title,
  options,
  selectedValue,
  onSelect,
  error,
  alignment,
  name,
}: QuizQuestionProps) {
  const t = useTranslations('quiz');

  return (
    <>
      <Heading
        variant="md"
        as="h1"
        id="find-your-style-quiz-title"
        className={styles.title}
      >
        {title}
      </Heading>

      <div
        className={clsx([
          alignment === 'grid' ? styles.optionsGrid : styles.optionsContainer,
        ])}
        role="radiogroup"
        aria-label={title}
      >
        {options.map((option) => (
          <div
            key={option.value}
            className={clsx(
              alignment === 'ladder' ? styles.wideAspect : styles.standardAspect
            )}
          >
            <QuizOption
              option={option}
              selectedValue={selectedValue}
              onSelect={() => onSelect(option.value)}
              label={t(option.labelKey)}
              error={Boolean(error)}
              labelPosition={
                alignment === 'grid' ? 'bottom-center' : 'top-left'
              }
              name={name}
            />
          </div>
        ))}
      </div>

      {error && (
        <div className={styles.errorContainer} role="alert">
          <AttentionIcon size={16} className={styles.attentionIcon} />
          <Caption variant="lg-regular" className={styles.errorText}>
            {error}
          </Caption>
        </div>
      )}
    </>
  );
}
